import React, { useMemo } from 'react';

/**
 * WindowDrawing — Parametric SVG drawing component
 * Generates a live schematic of a window/door based on configuration.
 * 
 * Props:
 *   openingType: 'sliding' | 'hinged' | 'fixed' | 'tilt-turn' | 'pocket'
 *   wingCount: number (1–6)
 *   width: number (cm)
 *   height: number (cm)
 *   hasScreen: boolean
 *   hasRollingShutter: boolean (מונו/מונובלוק — box on top)
 *   hasSlidingShutter: boolean (תריס הזזה — no box)
 *   fixedType: 'none' | 'top' | 'bottom' | 'both'
 *   shutterBoxHeight: number (cm, default 28)
 *   className: string (optional)
 */

// Map Hebrew opening types to drawing types
export const TYPE_MAP = {
    'חלונות הזזה': 'sliding',
    'דלתות הזזה': 'sliding',
    'חלונות פתיחה': 'hinged',
    'דלתות פתיחה': 'hinged',
    'חלונות קבועים': 'fixed',
    'חלונות כיס': 'pocket',
    'דלתות כיס': 'pocket',
    'ספיירקס': 'hinged',
    'רפפה': 'fixed',
    'מעקות': 'fixed',
    'מקלחונים': 'sliding',
    'רשתות': 'sliding',
    'תריסים': 'hinged',
};

// Drawing constants
const FRAME_THICKNESS = 8;
const SASH_INSET = 4;
const SCREEN_INSET = 3;
const DIM_OFFSET = 22;
const DIM_ARROW = 5;
const FIXED_SECTION_RATIO = 0.22;

export default function WindowDrawing({
    openingType = 'sliding',
    wingCount = 2,
    width = 120,
    height = 140,
    hasScreen = false,
    hasRollingShutter = false,
    hasSlidingShutter = false,
    fixedType = 'none',
    shutterBoxHeight = 28,
    className = '',
}) {
    const drawing = useMemo(() => {
        // Validate inputs
        const w = Math.max(width || 60, 30);
        const h = Math.max(height || 60, 30);
        const wings = Math.max(Math.min(wingCount || 2, 6), 1);
        const type = openingType || 'sliding';

        // Rolling shutter box height (proportional to drawing scale)
        const shutterH = hasRollingShutter ? (shutterBoxHeight / height) * h : 0;
        const totalDrawH = h + shutterH;
        
        // Add margins for dimension lines
        const marginLeft = DIM_OFFSET + 12;
        const marginRight = 12;
        const marginTop = 12;
        const marginBottom = DIM_OFFSET + 12;
        
        const svgW = w + marginLeft + marginRight;
        const svgH = totalDrawH + marginTop + marginBottom;
        
        // Frame origin (inner drawing area)
        const fx = marginLeft;
        const fy = marginTop + shutterH;
        const fw = w;
        const fh = h;

        // Fixed section calculations
        let fixedTopH = 0;
        let fixedBottomH = 0;
        let sashAreaY = fy;
        let sashAreaH = fh;
        
        if (fixedType === 'top' || fixedType === 'both') {
            fixedTopH = fh * FIXED_SECTION_RATIO;
        }
        if (fixedType === 'bottom' || fixedType === 'both') {
            fixedBottomH = fh * FIXED_SECTION_RATIO;
        }
        sashAreaY = fy + fixedTopH;
        sashAreaH = fh - fixedTopH - fixedBottomH;

        return { w, h, wings, type, shutterH, svgW, svgH, fx, fy, fw, fh, fixedTopH, fixedBottomH, sashAreaY, sashAreaH, marginLeft, marginTop };
    }, [openingType, wingCount, width, height, hasScreen, hasRollingShutter, hasSlidingShutter, fixedType, shutterBoxHeight]);

    const { w, h, wings, type, shutterH, svgW, svgH, fx, fy, fw, fh, fixedTopH, fixedBottomH, sashAreaY, sashAreaH } = drawing;

    // ─────────────── Drawing helpers ───────────────

    const renderFrame = () => (
        <rect
            x={fx} y={fy} width={fw} height={fh}
            fill="none" stroke="#1a1a2e" strokeWidth={3}
            rx={1}
        />
    );

    const renderShutterBox = () => {
        if (!hasRollingShutter) return null;
        const sy = fy - shutterH;
        return (
            <g>
                <rect x={fx} y={sy} width={fw} height={shutterH}
                    fill="#d1d5db" stroke="#6b7280" strokeWidth={2} rx={2} />
                <rect x={fx + 4} y={sy + 3} width={fw - 8} height={shutterH - 6}
                    fill="#e5e7eb" stroke="#9ca3af" strokeWidth={0.5} rx={1} />
                {/* Rolling lines */}
                {Array.from({ length: Math.floor(fw / 8) }, (_, i) => (
                    <line key={i}
                        x1={fx + 6 + i * 8} y1={sy + shutterH * 0.3}
                        x2={fx + 6 + i * 8} y2={sy + shutterH * 0.7}
                        stroke="#9ca3af" strokeWidth={0.5} />
                ))}
                <text x={fx + fw / 2} y={sy + shutterH / 2 + 3}
                    textAnchor="middle" fontSize={Math.min(shutterH * 0.4, 9)}
                    fill="#4b5563" fontWeight="bold">תריס</text>
            </g>
        );
    };

    const renderScreenTrack = () => {
        if (!hasScreen) return null;
        return (
            <rect
                x={fx + SCREEN_INSET} y={sashAreaY + SCREEN_INSET}
                width={fw - SCREEN_INSET * 2} height={sashAreaH - SCREEN_INSET * 2}
                fill="none" stroke="#22c55e" strokeWidth={1}
                strokeDasharray="4,3" rx={1} opacity={0.6}
            />
        );
    };

    const renderSlidingShutter = () => {
        if (!hasSlidingShutter) return null;
        // Draw shutter slats pattern on one side (indicating sliding shutter)
        const slatsX = fx + fw + 2;
        const slatsW = 6;
        const slatCount = Math.floor(fh / 6);
        return (
            <g opacity={0.5}>
                <rect x={slatsX} y={fy} width={slatsW} height={fh}
                    fill="#f3e8d0" stroke="#d97706" strokeWidth={0.8} rx={1} />
                {Array.from({ length: slatCount }, (_, i) => (
                    <line key={i}
                        x1={slatsX} y1={fy + (i + 1) * (fh / (slatCount + 1))}
                        x2={slatsX + slatsW} y2={fy + (i + 1) * (fh / (slatCount + 1))}
                        stroke="#d97706" strokeWidth={0.3} />
                ))}
                <text x={slatsX + slatsW / 2} y={fy + fh / 2}
                    textAnchor="middle" fontSize={5} fill="#92400e" fontWeight="bold"
                    transform={`rotate(-90, ${slatsX + slatsW / 2}, ${fy + fh / 2})`}
                >תריס</text>
            </g>
        );
    };

    const renderFixedSection = () => {
        if (fixedType === 'none') return null;
        
        const renderOneFixed = (y, h) => (
            <g>
                {/* Transom bar */}
                <line x1={fx} y1={y === fy ? y + h : y} x2={fx + fw} y2={y === fy ? y + h : y}
                    stroke="#1a1a2e" strokeWidth={2.5} />
                {/* X-cross in fixed section */}
                <line x1={fx + FRAME_THICKNESS} y1={y + SASH_INSET}
                    x2={fx + fw - FRAME_THICKNESS} y2={y + h - SASH_INSET}
                    stroke="#94a3b8" strokeWidth={0.8} />
                <line x1={fx + fw - FRAME_THICKNESS} y1={y + SASH_INSET}
                    x2={fx + FRAME_THICKNESS} y2={y + h - SASH_INSET}
                    stroke="#94a3b8" strokeWidth={0.8} />
                {/* Glass fill */}
                <rect x={fx + FRAME_THICKNESS} y={y + SASH_INSET}
                    width={fw - FRAME_THICKNESS * 2} height={h - SASH_INSET * 2}
                    fill="#dbeafe" opacity={0.3} />
            </g>
        );
        
        return (
            <g>
                {(fixedType === 'top' || fixedType === 'both') && fixedTopH > 0 && renderOneFixed(fy, fixedTopH)}
                {(fixedType === 'bottom' || fixedType === 'both') && fixedBottomH > 0 && renderOneFixed(fy + fh - fixedBottomH, fixedBottomH)}
            </g>
        );
    };

    const renderSashes = () => {
        const sashW = (fw - FRAME_THICKNESS * 2) / wings;
        const sx = fx + FRAME_THICKNESS;
        const sy = sashAreaY + FRAME_THICKNESS;
        const sh = sashAreaH - FRAME_THICKNESS * 2;
        
        const sashes = [];
        
        for (let i = 0; i < wings; i++) {
            const x = sx + i * sashW;
            const inset = SASH_INSET;
            const ix = x + inset;
            const iy = sy + inset;
            const iw = sashW - inset * 2;
            const ih = sh - inset * 2;
            
            // Sash frame rectangle
            sashes.push(
                <rect key={`sash-${i}`}
                    x={ix} y={iy} width={iw} height={ih}
                    fill="#dbeafe" fillOpacity={0.35}
                    stroke="#334155" strokeWidth={1.5} rx={0.5}
                />
            );

            // Divider line between sashes
            if (i > 0) {
                sashes.push(
                    <line key={`div-${i}`}
                        x1={x} y1={sy - SASH_INSET}
                        x2={x} y2={sy + sh + SASH_INSET}
                        stroke="#475569" strokeWidth={1.5} />
                );
            }

            // Type-specific indicators
            const cx = ix + iw / 2;
            const cy = iy + ih / 2;
            
            if (type === 'sliding' || type === 'pocket') {
                // Sliding arrows — alternate directions
                const goRight = (type === 'pocket') ? true : (i % 2 === 0);
                const arrowLen = Math.min(iw * 0.4, 25);
                const ax1 = cx - arrowLen / 2;
                const ax2 = cx + arrowLen / 2;
                
                sashes.push(
                    <g key={`arrow-${i}`} opacity={0.7}>
                        <line x1={ax1} y1={cy} x2={ax2} y2={cy}
                            stroke="#3b82f6" strokeWidth={1.5} />
                        {goRight ? (
                            <polygon points={`${ax2},${cy} ${ax2 - DIM_ARROW},${cy - DIM_ARROW * 0.7} ${ax2 - DIM_ARROW},${cy + DIM_ARROW * 0.7}`}
                                fill="#3b82f6" />
                        ) : (
                            <polygon points={`${ax1},${cy} ${ax1 + DIM_ARROW},${cy - DIM_ARROW * 0.7} ${ax1 + DIM_ARROW},${cy + DIM_ARROW * 0.7}`}
                                fill="#3b82f6" />
                        )}
                        {/* Track lines */}
                        <line x1={ix + 2} y1={cy - 3} x2={ix + iw - 2} y2={cy - 3}
                            stroke="#93c5fd" strokeWidth={0.5} strokeDasharray="2,2" />
                        <line x1={ix + 2} y1={cy + 3} x2={ix + iw - 2} y2={cy + 3}
                            stroke="#93c5fd" strokeWidth={0.5} strokeDasharray="2,2" />
                    </g>
                );
            } else if (type === 'hinged') {
                // Hinged — diagonal from hinge + arc
                const hingeOnLeft = (i % 2 === 0);
                const hx = hingeOnLeft ? ix : ix + iw;
                const oppositeX = hingeOnLeft ? ix + iw : ix;
                
                // Diagonal line from hinge to opposite corner (top)
                sashes.push(
                    <line key={`hinge-diag1-${i}`}
                        x1={hx} y1={iy}
                        x2={oppositeX} y2={cy}
                        stroke="#7c3aed" strokeWidth={0.8} strokeDasharray="3,2" />
                );
                // Diagonal line from hinge bottom
                sashes.push(
                    <line key={`hinge-diag2-${i}`}
                        x1={hx} y1={iy + ih}
                        x2={oppositeX} y2={cy}
                        stroke="#7c3aed" strokeWidth={0.8} strokeDasharray="3,2" />
                );
                
                // Small hinge indicators
                const hingeY1 = iy + ih * 0.25;
                const hingeY2 = iy + ih * 0.75;
                sashes.push(
                    <g key={`hinge-dots-${i}`}>
                        <circle cx={hx} cy={hingeY1} r={2} fill="#7c3aed" opacity={0.6} />
                        <circle cx={hx} cy={hingeY2} r={2} fill="#7c3aed" opacity={0.6} />
                    </g>
                );
            } else if (type === 'fixed') {
                // Fixed — X cross
                sashes.push(
                    <g key={`fixed-x-${i}`} opacity={0.4}>
                        <line x1={ix} y1={iy} x2={ix + iw} y2={iy + ih}
                            stroke="#64748b" strokeWidth={0.8} />
                        <line x1={ix + iw} y1={iy} x2={ix} y2={iy + ih}
                            stroke="#64748b" strokeWidth={0.8} />
                    </g>
                );
            } else if (type === 'tilt-turn') {
                // Tilt and turn — hinged diagonal + top tilt arc
                const hingeOnLeft = (i % 2 === 0);
                const hx = hingeOnLeft ? ix : ix + iw;
                const oppositeX = hingeOnLeft ? ix + iw : ix;
                
                // Diagonal lines (turn)
                sashes.push(
                    <line key={`tt-diag1-${i}`}
                        x1={hx} y1={iy} x2={oppositeX} y2={cy}
                        stroke="#7c3aed" strokeWidth={0.7} strokeDasharray="3,2" />
                );
                sashes.push(
                    <line key={`tt-diag2-${i}`}
                        x1={hx} y1={iy + ih} x2={oppositeX} y2={cy}
                        stroke="#7c3aed" strokeWidth={0.7} strokeDasharray="3,2" />
                );
                
                // Tilt arc at top
                const arcR = ih * 0.15;
                sashes.push(
                    <path key={`tt-arc-${i}`}
                        d={`M ${ix + 4} ${iy} Q ${cx} ${iy + arcR * 2} ${ix + iw - 4} ${iy}`}
                        fill="none" stroke="#f59e0b" strokeWidth={1}
                        strokeDasharray="2,2" />
                );
            }
        }
        
        return <g>{sashes}</g>;
    };

    const renderDimensionLines = () => {
        // Width dimension (bottom)
        const dimY = fy + fh + DIM_OFFSET;
        // Height dimension (left)
        const dimX = fx - DIM_OFFSET;
        
        const wLabel = `${w}`;
        const hLabel = `${h}`;
        
        return (
            <g className="dimensions" fill="#dc2626" stroke="#dc2626" fontSize={9} fontWeight="bold" fontFamily="monospace">
                {/* Width — bottom */}
                <line x1={fx} y1={dimY} x2={fx + fw} y2={dimY} strokeWidth={0.8} />
                {/* End ticks */}
                <line x1={fx} y1={dimY - 4} x2={fx} y2={dimY + 4} strokeWidth={0.8} />
                <line x1={fx + fw} y1={dimY - 4} x2={fx + fw} y2={dimY + 4} strokeWidth={0.8} />
                {/* Extension lines */}
                <line x1={fx} y1={fy + fh + 2} x2={fx} y2={dimY - 2} strokeWidth={0.3} strokeDasharray="2,2" />
                <line x1={fx + fw} y1={fy + fh + 2} x2={fx + fw} y2={dimY - 2} strokeWidth={0.3} strokeDasharray="2,2" />
                {/* Label */}
                <text x={fx + fw / 2} y={dimY + 11} textAnchor="middle" stroke="none">{wLabel}</text>
                
                {/* Height — left */}
                <line x1={dimX} y1={fy} x2={dimX} y2={fy + fh} strokeWidth={0.8} />
                <line x1={dimX - 4} y1={fy} x2={dimX + 4} y2={fy} strokeWidth={0.8} />
                <line x1={dimX - 4} y1={fy + fh} x2={dimX + 4} y2={fy + fh} strokeWidth={0.8} />
                <line x1={fx - 2} y1={fy} x2={dimX + 2} y2={fy} strokeWidth={0.3} strokeDasharray="2,2" />
                <line x1={fx - 2} y1={fy + fh} x2={dimX + 2} y2={fy + fh} strokeWidth={0.3} strokeDasharray="2,2" />
                <text x={dimX} y={fy + fh / 2}
                    textAnchor="middle" stroke="none"
                    transform={`rotate(-90, ${dimX}, ${fy + fh / 2})`}
                    dy={-5}>{hLabel}</text>
            </g>
        );
    };

    return (
        <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            className={`w-full h-auto ${className}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{ maxHeight: '220px' }}
        >
            {/* Background */}
            <rect x={0} y={0} width={svgW} height={svgH} fill="#fafbfc" rx={4} />
            
            {renderShutterBox()}
            {renderFrame()}
            {renderFixedSection()}
            {renderSashes()}
            {renderScreenTrack()}
            {renderSlidingShutter()}
            {renderDimensionLines()}
        </svg>
    );
}
